/**
 * Antwortet die Cloud? — getrennt davon, ob ein Sync gelingt.
 *
 * Ein Sync kann aus Gründen scheitern, die nichts mit der Verbindung zu tun
 * haben (er läuft noch, eine Zeile passt nicht). Hing das Offline-Symbol allein
 * am Sync, blieb es nach so einem Fehlschlag stehen, obwohl die Cloud längst
 * wieder antwortete (25.10.2026, Mac am Handy-Hotspot, erst ⌘R half).
 */

export const REACHABILITY_TIMEOUT_MS = 5_000

interface ReachabilityOptions {
  url?: string
  apiKey?: string
  timeoutMs?: number
}

/**
 * Fragt den Health-Endpunkt von Supabase Auth. Er braucht keine Sitzung und
 * belastet die Datenbank nicht. **Jede** HTTP-Antwort zählt als erreichbar —
 * auch eine Ablehnung heißt, dass am anderen Ende jemand ist. Nur ein Netzfehler
 * oder Schweigen bis zur Wartezeit heißt: nicht erreichbar. Ein hängender
 * Hotspot schweigt, statt abzulehnen; ohne Wartezeit käme nie eine Antwort.
 */
export async function checkSupabaseReachable({
  url = import.meta.env.VITE_SUPABASE_URL,
  apiKey = import.meta.env.VITE_SUPABASE_ANON_KEY,
  timeoutMs = REACHABILITY_TIMEOUT_MS,
}: ReachabilityOptions = {}): Promise<boolean> {
  if (!url) return false

  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), timeoutMs)
  try {
    // Mit Schlüssel antwortet der Endpunkt mit 200 statt 401 — sonst stünde
    // bei jeder Nachfrage ein roter Netzfehler in der Konsole.
    await fetch(`${url}/auth/v1/health`, {
      headers: { apikey: apiKey ?? '' },
      signal: controller.signal,
      cache: 'no-store',
    })
    return true
  } catch {
    return false
  } finally {
    clearTimeout(timer)
  }
}
